/**
 * A-07 «Не могу войти» и S-16 «Обращение в поддержку».
 *
 * ПС §15: категория «Не могу войти» доступна без авторизации.
 * BS §17.13: анонимный режим требует контакта для ответа.
 */
import { useState } from 'react'

import { api, unwrap } from '@shared/api/client'
import { Banner, Button, Field, Input, RadioGroup, Textarea, useToast } from '@shared/ui'

import { Screen, useGoBack } from '../../layouts/Screen'

type Category = 'cant_login' | 'account' | 'matches' | 'messages' | 'safety' | 'iphone_web_app' | 'other'

const CATEGORIES: { value: Category; label: string }[] = [
  { value: 'account', label: 'Аккаунт' },
  { value: 'matches', label: 'Совпадения' },
  { value: 'messages', label: 'Сообщения' },
  { value: 'safety', label: 'Безопасность' },
  { value: 'other', label: 'Другое' },
]

export function SupportScreen({ anonymous = false }: { anonymous?: boolean }): React.ReactElement {
  const goBack = useGoBack()
  const toast = useToast()

  const [category, setCategory] = useState<Category>(anonymous ? 'cant_login' : 'account')
  const [text, setText] = useState('')
  const [contact, setContact] = useState('')
  const [sent, setSent] = useState<string | null>(null)
  const [sending, setSending] = useState(false)

  const submit = async (): Promise<void> => {
    setSending(true)

    try {
      const ticket = unwrap(
        await api.POST('/support/tickets', {
          body: {
            category,
            text: text.trim(),
            ...(anonymous && contact.trim() ? { contact: contact.trim() } : {}),
          } as never,
        }),
      )
      setSent(ticket.id)
    } catch {
      toast.show('Не удалось отправить обращение', { tone: 'danger' })
    } finally {
      setSending(false)
    }
  }

  // A-08 «Обращение принято»
  if (sent) {
    return (
      <Screen title="Обращение отправлено" back narrow>
        <Banner tone="success" title="Обращение отправлено">
          Номер обращения: {sent.slice(-8)}.{' '}
          {anonymous ? 'Ответим на указанный контакт.' : 'Ответ придёт в этом разделе.'}
        </Banner>

        <div style={{ marginTop: 'var(--space-5)' }}>
          <Button variant="primary" fullWidth onClick={goBack}>
            {anonymous ? 'Вернуться ко входу' : 'Понятно'}
          </Button>
        </div>
      </Screen>
    )
  }

  /*
   * «Назад» ведёт туда, откуда пришли, — на форму входа с уже введённым
   * контактом (черновик A-02 живёт в сторе), а не на первый экран.
   */
  return (
    <Screen title={anonymous ? 'Не могу войти' : 'Обращение в поддержку'} back narrow>
      {anonymous ? (
        <Banner tone="neutral" icon="info">
          Войти можно по телефону или почте — любым подтверждённым способом. Если доступа нет, опишите
          ситуацию.
        </Banner>
      ) : null}

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-5)',
          marginTop: 'var(--space-4)',
        }}
      >
        {!anonymous ? (
          <Field label="Тема обращения">
            {() => (
              <RadioGroup
                name="support-category"
                value={category}
                onValueChange={setCategory}
                options={CATEGORIES}
              />
            )}
          </Field>
        ) : null}

        {anonymous ? (
          <Field label="Как с вами связаться" hint="Телефон или адрес почты">
            {({ id }) => (
              <Input
                id={id}
                value={contact}
                onChange={(event) => setContact(event.target.value)}
                placeholder="+7 900 000-00-00 или you@example.com"
              />
            )}
          </Field>
        ) : null}

        <Field label="Опишите ситуацию">
          {({ id }) => (
            <Textarea
              id={id}
              value={text}
              onChange={(event) => setText(event.target.value)}
              rows={6}
              maxLength={2000}
              placeholder="Что произошло и что вы уже пробовали"
            />
          )}
        </Field>

        <Button
          variant="primary"
          size="lg"
          fullWidth
          loading={sending}
          disabled={text.trim().length < 10 || (anonymous && !contact.trim())}
          onClick={() => void submit()}
        >
          Отправить
        </Button>
      </div>
    </Screen>
  )
}
